/**
 * Unified realtime channel for chat features
 * Combines messages, typing indicators, and unread counts into a single WebSocket connection
 */

import { useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/services/logger';
import type { RealtimeChannel } from '@supabase/supabase-js';

// Singleton channel manager to share connections across components
class ChatRealtimeManager {
  private channels = new Map<string, { channel: RealtimeChannel; refCount: number }>();
  private globalChannel: RealtimeChannel | null = null;
  private globalRefCount = 0;

  // Get or create room-specific channel
  getRoomChannel(roomId: string, userId: string): RealtimeChannel {
    const key = `room-${roomId}`;
    const existing = this.channels.get(key);
    
    if (existing) {
      existing.refCount++;
      return existing.channel;
    }

    const channel = supabase.channel(`chat-room-${roomId}`, {
      config: {
        presence: { key: userId },
        broadcast: { self: false },
      },
    });

    this.channels.set(key, { channel, refCount: 1 });
    return channel;
  }

  // Release room channel
  releaseRoomChannel(roomId: string): void {
    const key = `room-${roomId}`;
    const existing = this.channels.get(key);
    
    if (existing) {
      existing.refCount--;
      if (existing.refCount <= 0) {
        supabase.removeChannel(existing.channel);
        this.channels.delete(key);
      }
    }
  }

  // Get or create global channel for unread counts
  getGlobalChannel(userId: string): RealtimeChannel {
    if (this.globalChannel) {
      this.globalRefCount++;
      return this.globalChannel;
    }

    this.globalChannel = supabase.channel(`chat-global-${userId}`);
    this.globalRefCount = 1;
    return this.globalChannel;
  }

  // Release global channel
  releaseGlobalChannel(): void {
    this.globalRefCount--;
    if (this.globalRefCount <= 0 && this.globalChannel) {
      supabase.removeChannel(this.globalChannel);
      this.globalChannel = null;
    }
  }
}

// Singleton instance
export const chatRealtimeManager = new ChatRealtimeManager();

// Types for message handlers
export interface ChatMessage {
  id: string;
  chat_room_id: string;
  sender_user_id: string | null;
  content: string;
  content_type: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface TypingUser {
  oderId: string; // kept for backwards compat
  isTyping: boolean;
  displayName?: string;
}

export interface ChatRealtimeHandlers {
  onMessageInsert?: (message: ChatMessage) => void;
  onMessageUpdate?: (message: ChatMessage) => void;
  onMessageDelete?: (messageId: string) => void;
  onTypingChange?: (typingUsers: TypingUser[]) => void;
  onParticipantUpdate?: () => void;
}

interface UseChatRealtimeOptions {
  roomId: string;
  userId: string;
  displayName?: string;
  handlers: ChatRealtimeHandlers;
}

/**
 * Hook for unified chat realtime subscription
 * Combines messages, typing indicators into single channel per room
 */
export function useChatRealtime({
  roomId,
  userId,
  displayName,
  handlers,
}: UseChatRealtimeOptions) {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handlersRef = useRef(handlers);
  
  // Keep handlers ref updated
  handlersRef.current = handlers;

  // Set typing status
  const setTyping = useCallback(async (isTyping: boolean) => {
    if (!channelRef.current) return;

    try {
      await channelRef.current.track({
        oderId: userId,
        isTyping,
        displayName,
      });
    } catch (error) {
      logger.warn('Error setting typing status', { 
        error, 
        context: { feature: 'chat', function_name: 'setTyping' } 
      });
    }
  }, [userId, displayName]);

  // Handle typing with auto-stop
  const handleTyping = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    setTyping(true);

    typingTimeoutRef.current = setTimeout(() => {
      setTyping(false);
    }, 2000);
  }, [setTyping]);

  // Stop typing immediately
  const stopTyping = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    setTyping(false);
  }, [setTyping]);

  // Setup subscription
  useEffect(() => {
    const channel = chatRealtimeManager.getRoomChannel(roomId, userId);
    channelRef.current = channel;

    channel
      // Postgres changes for messages
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `chat_room_id=eq.${roomId}`,
        },
        (payload) => {
          logger.debug('New message received', { 
            context: { feature: 'chat' }, 
            data: { messageId: (payload.new as any)?.id }, 
            allowList: ['messageId'] 
          });
          handlersRef.current.onMessageInsert?.(payload.new as ChatMessage);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `chat_room_id=eq.${roomId}`,
        },
        (payload) => {
          logger.debug('Message updated', { 
            context: { feature: 'chat' }, 
            data: { messageId: (payload.new as any)?.id }, 
            allowList: ['messageId'] 
          });
          handlersRef.current.onMessageUpdate?.(payload.new as ChatMessage);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'chat_messages',
          filter: `chat_room_id=eq.${roomId}`,
        },
        (payload) => {
          logger.debug('Message deleted', { 
            context: { feature: 'chat' }, 
            data: { messageId: (payload.old as any)?.id }, 
            allowList: ['messageId'] 
          });
          const deletedId = (payload.old as { id: string }).id;
          handlersRef.current.onMessageDelete?.(deletedId);
        }
      )
      // Postgres changes for participant read status
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_participants',
          filter: `chat_room_id=eq.${roomId}`,
        },
        () => {
          handlersRef.current.onParticipantUpdate?.();
        }
      )
      // Presence for typing indicators
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState<TypingUser>();
        const typing: TypingUser[] = [];

        for (const [key, presences] of Object.entries(state)) {
          if (key !== userId && presences.length > 0) {
            const presence = presences[0];
            if (presence.isTyping) {
              typing.push(presence);
            }
          }
        }

        handlersRef.current.onTypingChange?.(typing);
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        logger.debug('User left chat room', { 
          context: { feature: 'chat' }, 
          data: { key }, 
          allowList: ['key'] 
        });
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            oderId: userId,
            isTyping: false,
            displayName,
          });
        }
      });

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      chatRealtimeManager.releaseRoomChannel(roomId);
    };
  }, [roomId, userId, displayName]);

  return {
    handleTyping,
    stopTyping,
  };
}

interface UseGlobalChatRealtimeOptions {
  userId: string;
  onNewMessage?: (message: ChatMessage) => void;
  onParticipantUpdate?: () => void;
}

/**
 * Hook for global unread count updates
 * Uses a single channel for all chat notifications
 */
export function useGlobalChatRealtime({
  userId,
  onNewMessage,
  onParticipantUpdate,
}: UseGlobalChatRealtimeOptions) {
  const handlersRef = useRef({ onNewMessage, onParticipantUpdate });
  handlersRef.current = { onNewMessage, onParticipantUpdate };

  useEffect(() => {
    if (!userId) return;

    const channel = chatRealtimeManager.getGlobalChannel(userId);

    channel
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
        },
        (payload) => {
          const newMessage = payload.new as ChatMessage;
          if (newMessage.sender_user_id !== userId) {
            handlersRef.current.onNewMessage?.(newMessage);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_participants',
        },
        () => {
          handlersRef.current.onParticipantUpdate?.();
        }
      )
      .subscribe();

    return () => {
      chatRealtimeManager.releaseGlobalChannel();
    };
  }, [userId]);
}

export default useChatRealtime;
